import { authSplitApi } from 'redux/helpers/slice.helpers';
import {
  TConfigSetChangeLogGroupDB,
  TConfigSetChangeLogGroupUI,
  TPropertyConfigEditNoteUI,
  TPropertyConfigSetCreateUI,
  TPropertyConfigSetEditUI,
  TPropertyConfigSetGetDB,
  TPropertyConfigSetGetUI,
} from 'data/types/propertyConfigSet.types';
import {
  configSetChangeLogConverter,
  propertyConfigNoteEditConverter,
  propertyConfigSetCreateConverter,
  propertyConfigSetEditConverter,
  propertyConfigSetGetConverter,
} from 'data/converters/propertyConfigSet․converters';

const BASE = 'property_manager/api/v2/animal-verification-configuration';

export const propertyConfigSetApi = authSplitApi('propertyConfigSetApi', ['property-config-sets']).injectEndpoints({
  endpoints: (build) => ({
    getPropertyConfigChangeLog: build.query<
      TConfigSetChangeLogGroupUI[],
      { animalVerificationConfigurationId: string }
    >({
      query(params) {
        return {
          url: `${BASE}/change-log`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (data: TConfigSetChangeLogGroupDB[]) => configSetChangeLogConverter.fromDb(data),
    }),
    getPropertyConfigs: build.query<TPropertyConfigSetGetUI[], { name: string } | null>({
      query(param) {
        return {
          url: `${BASE}/search`,
          method: 'GET',
          ...(param && {
            params: param,
          }),
        };
      },
      transformResponse: (data: TPropertyConfigSetGetDB[]) => {
        return (data || []).map((item) => propertyConfigSetGetConverter.fromDb(item));
      },
      providesTags: ['property-config-sets'],
    }),
    createPropertyConfig: build.mutation<string, TPropertyConfigSetCreateUI>({
      query(data) {
        return {
          url: `${BASE}`,
          method: 'POST',
          body: propertyConfigSetCreateConverter.toDb(data),
        };
      },
      invalidatesTags: ['property-config-sets'],
    }),
    editPropertyConfig: build.mutation<void, TPropertyConfigSetEditUI>({
      query(data) {
        return {
          url: `${BASE}`,
          method: 'PUT',
          body: propertyConfigSetEditConverter.toDb(data),
        };
      },
      invalidatesTags: ['property-config-sets'],
    }),
    deletePropertyConfig: build.mutation<
      void,
      {
        animalVerificationConfigurationId: string;
        replacingAnimalVerificationConfigurationId?: string;
      }
    >({
      query(params) {
        return {
          url: `${BASE}`,
          method: 'DELETE',
          body: params,
        };
      },
      invalidatesTags: ['property-config-sets'],
    }),
    createPropertyConfigNotes: build.mutation<void, TPropertyConfigEditNoteUI>({
      query(data) {
        return {
          url: `${BASE}/note`,
          method: 'POST',
          body: propertyConfigNoteEditConverter.toDb(data),
        };
      },
      invalidatesTags: ['property-config-sets'],
    }),
    updatePropertyConfigNotes: build.mutation<void, TPropertyConfigEditNoteUI>({
      query(data) {
        return {
          url: `${BASE}/note`,
          method: 'PUT',
          body: propertyConfigNoteEditConverter.toDb(data),
        };
      },
      invalidatesTags: ['property-config-sets'],
    }),
    deletePropertyConfigNotes: build.mutation<
      void,
      {
        animalVerificationConfigurationId: string;
        animalVerificationConfigurationNoteId: string;
        newConfigurationSetName?: string;
        propertyId?: string;
      }
    >({
      query(params) {
        return {
          url: `${BASE}/note`,
          method: 'DELETE',
          body: {
            ...params,
            noteId: params.animalVerificationConfigurationNoteId,
          },
        };
      },
      invalidatesTags: ['property-config-sets'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreatePropertyConfigMutation,
  useGetPropertyConfigsQuery,
  useGetPropertyConfigChangeLogQuery,
  useEditPropertyConfigMutation,
  useDeletePropertyConfigNotesMutation,
  useCreatePropertyConfigNotesMutation,
  useUpdatePropertyConfigNotesMutation,
  useDeletePropertyConfigMutation,
} = propertyConfigSetApi;
